import { useEffect, useRef, useState } from "react";

/**
 * Live countdown to the next 08:00 Dubai (Asia/Dubai, UTC+4) daily-code drop.
 * If it's already after 08:00 today, targets 08:00 tomorrow.
 */
function nextDropUTC(now) {
  // Dubai is UTC+4 (no DST). 08:00 Dubai == 04:00 UTC.
  const t = new Date(now);
  const target = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate(), 4, 0, 0));
  if (target.getTime() <= now.getTime()) target.setUTCDate(target.getUTCDate() + 1);
  return target;
}

function fmt(n) { return String(n).padStart(2, "0"); }

export default function BoostCountdown({ compact = false, onZero }) {
  const [now, setNow] = useState(() => new Date());
  const firedRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const target = nextDropUTC(now);
  const secsRaw = Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
  const h = Math.floor(secsRaw / 3600);
  const m = Math.floor((secsRaw % 3600) / 60);
  const s = secsRaw % 60;
  const soon = secsRaw < 3600;

  // fire onZero exactly once when countdown crosses the threshold; reset when it comes back up
  useEffect(() => {
    if (secsRaw <= 1 && !firedRef.current) {
      firedRef.current = true;
      try { onZero?.(); } catch {}
    } else if (secsRaw > 30) {
      firedRef.current = false;
    }
  }, [secsRaw, onZero]);

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 font-tabular text-[10px] uppercase tracking-[0.2em] ${
          soon ? "text-[#00FF41]" : "text-gold/85"
        }`}
        data-testid="boost-countdown"
        title="Next boost code drops at 08:00 Dubai time"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${soon ? "bg-[#00FF41]" : "bg-gold"} animate-pulse`} />
        {fmt(h)}:{fmt(m)}:{fmt(s)}
      </span>
    );
  }

  return (
    <div
      className="mt-3 flex items-center justify-between border border-gold/20 bg-gold/[0.03] px-3 py-2"
      data-testid="boost-countdown"
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${soon ? "bg-[#00FF41]" : "bg-gold"} animate-pulse`} />
        <span className="text-[10px] uppercase tracking-[0.25em] text-white/60">
          Next drop · 08:00 Dubai
        </span>
      </div>
      <span
        className={`font-tabular text-sm font-semibold tracking-[0.15em] ${
          soon ? "text-[#00FF41]" : "text-gold"
        }`}
      >
        {fmt(h)}:{fmt(m)}:{fmt(s)}
      </span>
    </div>
  );
}
